import classNames from 'classnames';

import { useExplorationContext } from '@/explore/exploration/exploration-context';
import { CloseButton, IconButton } from '@/components/button';
import { Tooltip } from '@/components/tooltip';
import { useMetadataContext } from '@/explore/metadata-context';
import { metricsByModelId } from '@/explore/utils/metrics';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { dereferencePipeline } from '@/explore/pipeline/utils';

import { InteractableProps } from '@/explore/utils/interactable';

import {
  Pipeline,
  Visualisation,
  isAggregatedVisualisation,
  isSimpleVisualisation,
} from '../../types';
import { VisualisationGraph } from './visualisation-graph';
import { getDereferencedPipelineFields, getFinalStateOrThrow } from '../../pipeline/state';
import { AggregatedVisualisationOptionsForm } from './visualisation-options-form/aggregated-visualisation-options-form';
import { SimpleVisualisationOptionsForm } from './visualisation-options-form/simple-visualisation-options-form';

import { getQueryForVisualisation } from './utils';

import styles from './visualisation.module.scss';

interface VisualisationPanelProps {
  idx: number;
  pipeline: Pipeline;
  visualisation: Visualisation;
  onClose(): void;
  onEdit(): void;
  onVisualisationChange: (visualisation: Visualisation) => void;
  isInView: boolean;
  isResized: boolean;
  hideHeader?: boolean;
}

export const VisualisationPanel = (props: VisualisationPanelProps) => {
  const {
    idx,
    visualisation,
    isResized,
    onClose,
    onEdit,
    onVisualisationChange,
    hideHeader = false,
  } = props;

  const { models, metrics } = useMetadataContext();
  const { exploration, getVariables } = useExplorationContext();

  const pipeline = dereferencePipeline(props.pipeline, exploration);
  const stateContext = { models, variables: getVariables(), metrics };
  const fields = getDereferencedPipelineFields(pipeline, stateContext);
  const { groups } = getQueryForVisualisation({
    pipeline: props.pipeline,
    fields,
    visualisation,
    exploration,
  });

  const showOptions =
    !hideHeader &&
    ((isAggregatedVisualisation(visualisation) &&
      visualisation.aggregation.aggregations.length === 1 &&
      visualisation.aggregation.groups.length < 2) ||
      (isSimpleVisualisation(visualisation) &&
        groups.length === 0 &&
        visualisation.mainAxisKey !== undefined));

  const pipelineState = getFinalStateOrThrow(pipeline, {
    models,
    variables: getVariables(),
    metrics,
  });

  const availableMetrics = metricsByModelId(metrics, pipelineState.model.modelId);

  const chartOptions = showOptions ? (
    isAggregatedVisualisation(visualisation) ? (
      <AggregatedVisualisationOptionsForm
        visualisation={visualisation}
        onChange={onVisualisationChange}
        metrics={availableMetrics}
        fields={pipelineState.fields}
        excludeAggregationTypes={['earliest', 'latest', 'first', 'last']}
      />
    ) : (
      <SimpleVisualisationOptionsForm
        visualisation={visualisation}
        onChange={onVisualisationChange}
        fields={pipelineState.fields}
      />
    )
  ) : null;

  return (
    <div className={classNames(styles.visualisationCard, 'ph-no-capture')}>
      <HideInEmbedded>
        <Header floating={!showOptions} onClose={onClose} onEdit={onEdit}>
          {chartOptions}
        </Header>
      </HideInEmbedded>
      <VisualisationGraph
        idx={idx}
        pipeline={props.pipeline}
        fields={pipelineState.fields}
        visualisation={visualisation}
        onVisualisationChange={onVisualisationChange}
        variables={getVariables()}
        isInView={props.isInView}
        isResized={isResized}
        showingOptions={showOptions}
      />
    </div>
  );
};

interface VisualisationLoaderProps {
  isResized?: boolean;
}

export const VisualisationLoader = ({ isResized = false }: VisualisationLoaderProps) => (
  <div
    className={classNames(styles.visualisationLoader, {
      [styles.isResized]: isResized,
    })}>
    <div className={styles.loaderPulse} />
  </div>
);

interface HeaderProps {
  children?: React.ReactNode;
  floating: boolean;
  onClose: () => void;
  onEdit: () => void;
}

const Header = ({ children, floating, onClose, onEdit }: HeaderProps) => (
  <div className={classNames(styles.header, floating && styles.floating)} {...InteractableProps}>
    <div className={styles.toolbar}>{children}</div>
    <div className={styles.rightToolbar}>
      <Tooltip content="Edit visualisation">
        <div className={styles.headerBtn}>
          <IconButton
            icon="Edit2"
            iconSize="regular"
            onClick={onEdit}
            className={styles.editIcon}
          />
        </div>
      </Tooltip>
      <Tooltip content="Remove visualisation">
        <div className={styles.headerBtn}>
          <CloseButton title={undefined} iconSize="regular" onClick={onClose} />
        </div>
      </Tooltip>
    </div>
  </div>
);
